import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { Page, PageBackgroundColor } from 'src/components/layouts/Page'
import { Text } from 'src/components/molecules/sanity/Content'

import { NotFound } from 'src/pages/404/404'
import { NavigationContext } from 'src/types/account'
import { formatDate } from 'src/utils/date'
import { getSanityImageUrl } from 'src/utils/sanity'
import { trpc } from 'src/utils/trpc'

import { PostBody } from './PostBody/PostBody'
import { PostHeroRebrand } from './PostHeroRebrand/PostHeroRebrand'
import { PostNavigation } from './PostNavigation'

const Post: NextPage = () => {
  const router = useRouter()

  const { slug: slugParts, preview: previewParam } = router.query as {
    slug: string[]
    preview?: string
  }
  const slug = [...slugParts].reduce(
    (acc, part, index) => (index ? `${acc}/${part}` : part),
    ''
  )
  const preview = previewParam === 'true'

  const {
    data: post,
    isLoading,
    isFetching
  } = trpc.useQuery(['sanity.getPostBySlug', { slug, preview }], {
    keepPreviousData: true
  })

  const isPublished = Boolean(post?.publishedAt)

  useEffect(() => {
    if (!isFetching && post && post.slug.current !== slug) {
      router.replace({ pathname: `/blog/${post.slug.current}` })
    }
  }, [isFetching, post, router, slug])

  useEffect(() => {
    if (preview && post && post.slug.current === slug && isPublished) {
      router.replace({ pathname: `/blog/${slug}` }, undefined, {
        shallow: true
      })
    }
  }, [isPublished, post, preview, router, slug])

  if (isLoading || isFetching) {
    return null
  }

  // Redirect if page not found
  if (!post) {
    return <NotFound />
  }

  const {
    body,
    description,
    lastPost,
    mainImage,
    nextPost,
    publishedAt,
    summary,
    title,
    estimatedReadingTime
  } = post

  const date = formatDate({
    date: publishedAt,
    format: 'DD MMM YYYY',
    isUtc: true
  })

  return (
    <Page
      navigationContext={NavigationContext.Public}
      title={title}
      metaTitle={title}
      metaDescription={description}
      image={getSanityImageUrl(mainImage, 1200, 630)}
      isFullWidth
      showBreadcrumbs={false}
      backgroundColor={PageBackgroundColor.white}
    >
      <>
        {preview && !isPublished && (
          <div className='fixed top-0 left-0 right-0 bg-red-500 text-white py-2.5 px-4 text-center z-9999 text-sm font-bold'>
            Preview Mode Active - You are viewing a draft post
          </div>
        )}
        <PostHeroRebrand
          title={title}
          postDate={date}
          imageUrl={mainImage}
          readingTime={`${estimatedReadingTime} mins read`}
        />
        <article className='mx-auto mt-12 mb-16 w-full max-w-(--content-max-width)'>
          {summary && (
            <div className='mx-auto mb-8 w-full max-w-[1280px] px-4 lg:px-0'>
              <TypographyRebrand variant='introduction' color='forestDeep'>
                <Text
                  value={summary}
                  components={{
                    block: ({ children }) => (
                      <p className='mb-4 last:mb-0'>{children}</p>
                    )
                  }}
                />
              </TypographyRebrand>
            </div>
          )}
          <PostBody body={body} />
          <div className='mx-auto mt-12 w-full max-w-[1280px] px-4 lg:mt-16 lg:px-0'>
            <PostNavigation
              lastPostSlug={lastPost?.slug?.current}
              nextPostSlug={nextPost?.slug?.current}
            />
          </div>
        </article>
      </>
    </Page>
  )
}

export default Post
